import {useRef} from 'react';
import {SimpleGrid, Badge, Text} from '@mantine/core';
import {NavLink} from 'react-router-dom';
import {useProviderMappings} from '../hooks/useProviderMappings';
import {useProviderMappingsSelection} from '../hooks/useProviderMappingsSelection';
import {ProviderMapping} from '../types';
import {MantineDataTable, TableSearchForm} from '@flexinet/ui-components';
import {DataTableColumn} from 'mantine-datatable';
import {DataTableTokenPagination} from '../../../components/DataTableTokenPagination.tsx';

export const RecordsTable = ({ productId, customerId, priceId }: { productId?: string, customerId?: string, priceId?: string }) => {
    const {selectedRecords, setSelectedRecords} = useProviderMappingsSelection();
    
    const {
        items,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useProviderMappings(productId, customerId, priceId);
    
    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Items are already filtered by the hook, no need for client-side filtering
    const filteredItems = items;

    const getSyncStatusColor = (status: string) => {
        switch (status) {
            case 'Synced': return 'green';
            case 'Pending': return 'yellow';
            case 'Failed': return 'red';
            case 'Conflict': return 'orange';
            default: return 'gray';
        }
    };

    const columns: DataTableColumn<ProviderMapping>[] = [
        {
            accessor: 'entityType',
            title: 'Entity Type',
            render: (record: ProviderMapping) => (
                <Badge variant="light">{record.entityType}</Badge>
            )
        },
        {
            accessor: 'billingProvider.name',
            title: 'Billing Provider',
            render: (record: ProviderMapping) => (
                <NavLink 
                    to={`/billing-providers/${record.billingProviderId}`}
                    style={{ textDecoration: 'none', fontWeight: 500, color: 'var(--mantine-color-blue-6)' }}
                >
                    {record.billingProvider?.name || record.billingProviderId}
                </NavLink>
            )
        },
        {
            accessor: 'providerEntityId',
            title: 'Provider Entity ID',
            render: (record: ProviderMapping) => (
                <Text c="dimmed" size="sm">{record.providerEntityId}</Text>
            )
        },
        {
            accessor: 'syncStatus',
            title: 'Sync Status',
            render: (record: ProviderMapping) => (
                <Badge color={getSyncStatusColor(record.syncStatus)} variant="light">
                    {record.syncStatus}
                </Badge>
            )
        },
        {
            accessor: 'lastSyncAt',
            title: 'Last Sync',
            render: (record: ProviderMapping) => (
                <Text size="sm" c="dimmed">
                    {record.lastSyncAt ? new Date(record.lastSyncAt).toLocaleString() : 'Never'}
                </Text>
            )
        }
    ];

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword} />
            
            <MantineDataTable
                columns={columns}
                records={filteredItems}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                noRecordsText={productId ? "No billing provider mappings found for this product" : customerId ? "No billing provider mappings found for this customer" : priceId ? "No billing provider mappings found for this price" : "No provider mappings found"}
            />

            {!productId && !customerId && !priceId && (
                <DataTableTokenPagination 
                    totalCount={filteredItems.length.toString()} 
                    nextToken={nextToken} 
                    handleLoadMore={fetchRecords} 
                />
            )}
        </SimpleGrid>
    );
};